export interface ProblemData {
  problem: string;
  status: string;
  practitioner: string;
  date: string;
  patientId: string;
}

type RawProblem = ProblemData & { authored: Date };

function parseProblemStatus(statusItem?: fhir5.QuestionnaireResponseItem): string {
  let status = "aberta";
  const coding = statusItem?.answer?.[0]?.valueCoding;
  if (!coding) return status;
  const display = coding.display;
  if (display === "Melhora") status = "melhora";
  else if (display === "Estável") status = "estável";
  else if (display === "Piora") status = "piora";
  return status;
}

export function processEntriesToProblems(
  entries: unknown[],
  getPractitionerName: (ref: string) => string
): ProblemData[] {
  const problemMap = new Map<string, RawProblem>();

  for (const entry of entries) {
    const resource = (entry as { resource?: fhir5.QuestionnaireResponse })?.resource;
    if (!resource || resource.resourceType !== "QuestionnaireResponse") continue;

    const problemsItem = resource.item?.find(
      (item) => item.linkId === "f075e9cc-c1c4-44ac-db18-035cbc5453d4"
    );
    if (!problemsItem?.item) continue;

    const patientId = resource.subject?.reference || "unknown";
    const practitionerName = getPractitionerName(resource.author?.reference || "");
    const authoredDate = resource.authored ? new Date(resource.authored) : new Date(0);
    const date = authoredDate.toLocaleDateString();
    const seenInThisQuestionnaire = new Set<string>();

    for (const problemGroup of problemsItem.item) {
      if (!problemGroup.item || problemGroup.linkId !== "8e2c9b72-bcee-4c0f-ecb2-8e4fc2866cd4") {
        continue;
      }

      const problemItem = problemGroup.item.find(
        (item) => item.linkId === "4f96c740-f739-4758-8324-b5fd7893fcbd"
      );
      const problemText = problemItem?.answer?.[0]?.valueString;
      if (!problemText) continue;

      const statusItem = problemGroup.item.find(
        (item) => item.linkId === "2972b2f4-a634-4277-8200-96ce9111dc3d"
      );
      const status = parseProblemStatus(statusItem);

      const uniqueKey = status === "aberta" ? problemText : `${problemText}-${status}`;
      if (seenInThisQuestionnaire.has(uniqueKey)) continue;
      seenInThisQuestionnaire.add(uniqueKey);

      const key =
        status === "aberta"
          ? `${patientId}-${problemText}`
          : `${patientId}-${problemText}-${status}`;

      const existing = problemMap.get(key);
      if (!existing || authoredDate > existing.authored) {
        problemMap.set(key, {
          problem: problemText,
          status,
          practitioner: practitionerName,
          date,
          patientId,
          authored: authoredDate,
        });
      }
    }
  }

  const patientLatestAuthored = new Map<string, number>();
  for (const v of problemMap.values()) {
    const t = v.authored.getTime();
    const prev = patientLatestAuthored.get(v.patientId) || 0;
    if (t > prev) patientLatestAuthored.set(v.patientId, t);
  }

  const groups = new Map<string, RawProblem[]>();
  for (const value of problemMap.values()) {
    const key = `${value.patientId}|${value.problem}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(value);
  }

  const parityProblems: ProblemData[] = [];
  for (const [, items] of groups.entries()) {
    if (items.length === 1) {
      const [one] = items;
      parityProblems.push({
        problem: one.problem,
        status: one.status,
        practitioner: one.practitioner,
        date: one.date,
        patientId: one.patientId,
      });
      continue;
    }

    const sorted = items.sort((a, b) => b.authored.getTime() - a.authored.getTime());
    const isPair = items.length % 2 === 0;
    const withStatus = sorted.filter((it) => it.status && it.status !== "aberta");
    const withoutStatus = sorted.filter((it) => !it.status || it.status === "aberta");
    const latestAuthoredTs = patientLatestAuthored.get(sorted[0].patientId);
    const latestBelongsToCurrentQR =
      latestAuthoredTs !== undefined && sorted[0].authored.getTime() === latestAuthoredTs;

    const push = (item: RawProblem) =>
      parityProblems.push({
        problem: item.problem,
        status: item.status,
        practitioner: item.practitioner,
        date: item.date,
        patientId: item.patientId,
      });

    if (latestBelongsToCurrentQR) {
      for (const item of sorted) push(item);
    } else if (isPair) {
      if (withStatus.length > 0) push(withStatus[0]);
      else push(sorted[0]);
    } else if (withStatus.length > 0) {
      push(withStatus[0]);
      if (withoutStatus.length > 0) push(withoutStatus[0]);
    } else {
      push(sorted[0]);
    }
  }

  return parityProblems;
}
